<template>
  <div class="page">
    <div class="container">
      <div class="content">
        <div class="container news-container">
          <div class="news-grid">
            <a
              v-for="item in articles"
              :key="item.objectId"
              :href="item.contentUrl"
              target="_blank"
              rel="noopener"
              class="news-link"
            >
              <article class="news-card">
                <div class="card-media">
                  <img
                    :src="safeImageUrl(item.imageUrl)"
                    alt="新闻缩略图"
                    class="card-img"
                    referrerpolicy="no-referrer"
                    @error="onArticleImgError"
                  />
                  <span class="badge">文章</span>
                </div>

                <div class="card-content">
                  <h3 class="card-title">{{ item.title }}</h3>
                  <div class="card-meta">
                    <span class="meta-item"
                      ><i class="icon-calendar"></i
                      >{{ formatDate(item.pubTime) }}</span
                    >
                    <span class="meta-item"
                      ><i class="icon-eye"></i>{{ item.viewNumber }}</span
                    >
                    <span class="meta-item"
                      ><i class="icon-like"></i>{{ item.likeNumber }}</span
                    >
                  </div>
                </div>
              </article>
            </a>
          </div>

          <!-- 换页器 -->
          <div class="pager" v-if="totalPages > 1">
            <span class="pager-info"
              >共 <b>{{ totalCount }}</b> 条记录</span
            >

            <button
              class="pager-btn"
              :disabled="currentPage === 1"
              @click="goPrev"
            >
              上一页
            </button>

            <div v-for="btn in pageButtons" :key="btn + '-' + currentPage">
              <button
                v-if="btn !== '...'"
                class="pager-btn"
                :class="{ active: btn === currentPage }"
                @click="goPage(btn)"
              >
                {{ btn }}
              </button>
              <span v-else class="pager-ellipsis">...</span>
            </div>

            <button
              class="pager-btn"
              :disabled="currentPage === totalPages"
              @click="goNext"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import defaultArticleImage from "./images/article_1.png";

export default {
  name: "IndexPage",
  props: {
    // 从父组件传递图片处理函数
    safeImageUrl: {
      type: Function,
      default: (url) => url || defaultArticleImage
    }
  },
  data() {
    return {
      articles: [],
      defaultArticleImage,
      currentPage: 1,
      pageSize: 9,
      totalCount: 0,
    };
  },
  mounted() {
    this.computePageSize(1);
    this.fetchNewsList(1);
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
    pageButtons() {
      const total = this.totalPages;
      const cur = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      const start = Math.max(2, cur - 1);
      const end = Math.min(total - 1, cur + 1);
      if (start > 2) pages.push("...");
      for (let p = start; p <= end; p++) pages.push(p);
      if (end < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    },
  },
  methods: {
    onArticleImgError(e) {
      if (!e || !e.target) return;
      e.target.src = this.defaultArticleImage;
      e.target.classList.add("img-error");
    },
    
    computePageSize() {
      const w = window.innerWidth || document.documentElement.clientWidth;
      let columns = 3;
      if (w >= 1400) columns = 4;
      else if (w >= 992) columns = 3;
      else if (w > 576) columns = 2;
      else columns = 2;
      const rows = w <= 576 ? 6 : 3;
      this.pageSize = columns * rows;
    },
    
    handleResize() {
      const oldSize = this.pageSize;
      this.computePageSize();
      if (this.pageSize !== oldSize) {
        this.currentPage = 1;
        this.fetchNewsList(1);
      }
    },
    
    async fetchNewsList(page = 1) {
      try {
        const Bmob = this.$bmob;
        if (!Bmob) {
          console.warn(
            "Bmob 未挂载到全局"
          );
          return;
        }
        const size = this.pageSize;
        const offset = (page - 1) * size;

        const query = Bmob.Query("TableWebNews");
        query.order("-pubTime");
        if (typeof query.limit === "function") query.limit(size);
        if (typeof query.skip === "function") query.skip(offset);
        const list = await query.find();

        let total = 0;
        try {
          const countQuery = Bmob.Query("TableWebNews");
          total = await countQuery.count();
        } catch (e) {
          total = offset + (Array.isArray(list) ? list.length : 0);
        }

        const getVal = (obj, key) =>
          typeof obj.get === "function" ? obj.get(key) : obj[key];
        this.articles = (list || []).map((item) => ({
          objectId: item.objectId,
          title: getVal(item, "title") || "",
          imageUrl: getVal(item, "imageUrl") || "",
          pubTime: getVal(item, "pubTime") || "",
          likeNumber: getVal(item, "likeNumber") ?? 0,
          viewNumber: getVal(item, "viewNumber") ?? 0,
          contentUrl: getVal(item, "contentUrl") || "#",
        }));
        this.totalCount = Number.isFinite(total) ? total : 0;
        this.currentPage = page;

        if (this.currentPage > this.totalPages) {
          this.fetchNewsList(this.totalPages);
        }
      } catch (err) {
        console.error("获取新闻列表失败:", err);
      }
    },
    
    goPage(p) {
      if (p === "..." || p === this.currentPage) return;
      this.fetchNewsList(p);
    },
    
    goPrev() {
      if (this.currentPage > 1) this.fetchNewsList(this.currentPage - 1);
    },
    
    goNext() {
      if (this.currentPage < this.totalPages)
        this.fetchNewsList(this.currentPage + 1);
    },
    
    formatDate(v) {
      try {
        if (!v) return "";
        const d = typeof v === "number" ? new Date(v) : new Date(v);
        if (isNaN(d)) return String(v);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      } catch {
        return String(v || "");
      }
    },
  },
};
</script>

<style scoped>
.page {
  min-height: 100vh;
}

.container {
  padding: 20px;
}

.news-container {
  margin-top: 20px;
}

/* 新闻网格样式 */
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
@media (min-width: 1400px) {
  .news-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
@media (max-width: 992px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 576px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

.news-link {
  display: block;
  height: 100%;
  text-decoration: none;
  color: inherit;
}
.news-link:hover {
  text-decoration: none;
  color: inherit;
}

.news-card {
  position: relative;
  border: 1px solid var(--card-border);
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(20, 23, 45, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
}
.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(20, 23, 45, 0.12);
}

.card-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
}
.card-media::before {
  content: "";
  display: block;
  padding-top: 56.25%;
}
.card-media::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 60%,
    rgba(0, 0, 0, 0.06) 100%
  );
  pointer-events: none;
}
.card-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.img-error {
  filter: grayscale(100%);
}
.badge {
  position: absolute;
  left: 12px;
  top: 12px;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--primary), var(--primary-2));
  color: #fff;
  box-shadow: 0 4px 10px rgba(42, 199, 255, 0.4);
}

.card-content {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  background: #fff;
}
.card-title {
  font-size: 0.98rem;
  line-height: 1.5;
  color: #1f2937;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: calc(1.5em * 2);
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-muted);
  font-size: 12px;
  margin-top: auto;
}
.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.icon-calendar::before {
  content: "📅";
}
.icon-eye::before {
  content: "👁️";
}
.icon-like::before {
  content: "👍";
}

/* 换页器 */
.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  flex-wrap: wrap;
}
.pager-info {
  color: #666;
  font-size: 13px;
  margin-right: 8px;
}
.pager-info b {
  color: #ff6a00;
  font-weight: 600;
}
.pager-btn {
  padding: 6px 12px;
  border: 1px solid #e9eef5;
  background: #fff;
  color: #333;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 36px;
  text-align: center;
}
.pager-btn:hover:not(:disabled) {
  border-color: #ffc799;
  background: #fff7f0;
}
.pager-btn.active {
  background: #ff6a00;
  color: #fff;
  border-color: #ff6a00;
}
.pager-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.pager-ellipsis {
  padding: 6px 8px;
  color: #999;
  font-size: 13px;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .news-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

@media screen and (max-width: 576px) {
  .container {
    padding: 10px;
  }
}
</style>